// ----------------------------------------------------------------------------
// Block: Media Gallery <media-gallery-block>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Renders the parent product's media (product.media) as a primary Swiper
// viewport with a desktop thumbnail rail / mobile position indicator.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:variant:changed' → When the selected variant has a featured
//                                   media item, that item becomes displayed.
// ----------------------------------------------------------------------------
// Gotchas:
// - Desktop primary viewport is not a scrolling region: touch/mouse dragging
//   is disabled above the tablet breakpoint via Swiper's `breakpoints`, but
//   the same Swiper instance stays alive so `slideTo()` still drives thumbnail
//   presses and variant-bound media changes.
// - Only native Shopify-hosted `video` media gets the custom play/pause
//   control; `external_video` / `model` render with their own default chrome.
// - Each instance owns its own Swiper + video state; nothing is shared across
//   multiple Media Gallery instances on the same page.
// ----------------------------------------------------------------------------

// Helpers
import Swiper from 'swiper';
import { Pagination, A11y } from 'swiper/modules';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import registerCustomElement from 'scripts/helpers/register-custom-element';

// Styles
import './_media-gallery.scss';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/a11y';

// -------------------------------------
// Constants
// -------------------------------------

var TABLET_BREAKPOINT = 768;
var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// -------------------------------------
// Web Component
// -------------------------------------

class MediaGalleryBlock extends iamFormElement {
  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    this.autoplayVideo = this.dataset.autoplayVideo === 'true';
    this.mediaLabel = this.dataset.mediaLabel || 'Show media';
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this.viewportEl = this.querySelector('.media-gallery__viewport');
    this.thumbsEl = this.querySelector('[data-media-gallery-thumbs]');
    this.paginationEl = this.querySelector('.media-gallery__pagination');
    this.thumbs = Array.from(this.querySelectorAll('[data-media-thumb]'));
    if (!this.viewportEl) return;
    this.activeVideo = null;
    this.swiper = this.initSwiper();
    this.bindThumbs();
    this.bindPlayPauseButtons();
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "media-gallery.js"
    });
  }

  /**
   * Init the primary viewport Swiper instance
   */
  initSwiper() {
    return new Swiper(this.viewportEl, {
      modules: [Pagination, A11y],
      slidesPerView: 1.3,
      spaceBetween: 8,
      loop: false,
      watchOverflow: true,
      pagination: this.paginationEl ? {
        el: this.paginationEl,
        clickable: true
      } : false,
      a11y: {
        paginationBulletMessage: "".concat(this.mediaLabel, " {{index}}")
      },
      breakpoints: {
        [TABLET_BREAKPOINT]: {
          slidesPerView: 1,
          spaceBetween: 0,
          allowTouchMove: false
        }
      },
      on: {
        slideChange: swiper => this.onSlideChange(swiper),
        init: swiper => this.onSlideChange(swiper)
      }
    });
  }

  /**
   * Wire thumbnail presses to the "show media item" action
   */
  bindThumbs() {
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        var index = parseInt(thumb.dataset.index, 10);
        if (!isNaN(index)) this.swiper.slideTo(index);
      });
    });
  }

  /**
   * Wire the custom video play/pause controls
   */
  bindPlayPauseButtons() {
    this.querySelectorAll('[data-media-play-pause]').forEach(button => {
      button.addEventListener('click', () => {
        var video = button.previousElementSibling;
        if (!video || video.tagName !== 'VIDEO') return;
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    });
  }

  /**
   * On Slide Change: sync thumbnail selection + scroll it into view,
   * and toggle video playback for the outgoing/incoming slide.
   */
  onSlideChange(swiper) {
    var activeIndex = swiper.activeIndex;
    this.thumbs.forEach(thumb => {
      var isSelected = parseInt(thumb.dataset.index, 10) === activeIndex;
      thumb.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      if (isSelected) this.scrollThumbIntoView(thumb);
    });
    this.updateActiveVideo(swiper.slides[activeIndex]);
  }

  /**
   * Scroll the selected thumbnail into view within the rail only, so the
   * page itself never scrolls. `scrollIntoView` walks every scrollable
   * ancestor, so once the rail scrolls it would jump the page too -
   * `scrollBy` on the rail keeps the effect scoped to the rail.
   */
  scrollThumbIntoView(thumb) {
    var rail = this.thumbsEl;
    if (!rail || rail.scrollHeight <= rail.clientHeight) return;
    var t = thumb.getBoundingClientRect();
    var r = rail.getBoundingClientRect();
    if (t.top >= r.top && t.bottom <= r.bottom) return;
    var delta = t.top < r.top ? t.top - r.top : t.bottom - r.bottom;
    rail.scrollBy({
      top: delta,
      behavior: this.reducedMotion ? 'auto' : 'smooth'
    });
  }

  /**
   * Pause the outgoing video (if any) and, if the newly active slide is a
   * video, play it muted when Autoplay video is enabled.
   */
  updateActiveVideo(activeSlide) {
    if (this.activeVideo && !this.activeVideo.paused) {
      this.activeVideo.pause();
    }
    var video = activeSlide === null || activeSlide === void 0 ? void 0 : activeSlide.querySelector('video.media-gallery__video');
    this.activeVideo = video || null;
    if (video && this.autoplayVideo) {
      video.play();
    }
  }

  /**
   * On Variant Change: jump to the variant's featured media, if any
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    if (!(variant !== null && variant !== void 0 && variant.featured_media)) return;
    var targetSlide = this.querySelector(".media-gallery__slide[data-media-id=\"".concat(variant.featured_media.id, "\"]"));
    if (!targetSlide) return;
    var index = Array.from(this.viewportEl.querySelectorAll('.media-gallery__slide')).indexOf(targetSlide);
    if (index > -1) this.swiper.slideTo(index);
  }
}

// Sync the play/pause control state with the underlying <video> element
document.addEventListener('play', event => {
  var _event$target, _event$target$querySe;
  var button = (_event$target = event.target) === null || _event$target === void 0 || (_event$target = _event$target.parentElement) === null || _event$target === void 0 || (_event$target$querySe = _event$target.querySelector) === null || _event$target$querySe === void 0 ? void 0 : _event$target$querySe.call(_event$target, '[data-media-play-pause]');
  if (button) setPlayPauseState(button, true);
}, true);
document.addEventListener('pause', event => {
  var _event$target2, _event$target2$queryS;
  var button = (_event$target2 = event.target) === null || _event$target2 === void 0 || (_event$target2 = _event$target2.parentElement) === null || _event$target2 === void 0 || (_event$target2$queryS = _event$target2.querySelector) === null || _event$target2$queryS === void 0 ? void 0 : _event$target2$queryS.call(_event$target2, '[data-media-play-pause]');
  if (button) setPlayPauseState(button, false);
}, true);
function setPlayPauseState(button, isPlaying) {
  button.classList.toggle('is-playing', isPlaying);
  button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
  button.setAttribute('aria-label', isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel);
}

// Define the custom element
registerCustomElement('media-gallery-block', MediaGalleryBlock);